import { cn } from '@/lib/utils/cn';
import { useTtsPlanUsers, useVoicePlanUsers } from '../api';

interface PlanUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which plan family this modal is listing users for. */
  kind: 'voice' | 'tts';
  planId: number | null;
  planName?: string;
}

/**
 * Superadmin-panel-only modal (Voice Pricing + TTS Pricing "Users" column).
 * Same fixed-overlay + table pattern as Reports → Downloads'
 * BulkCampaignsModal, so it looks and behaves consistently across the app.
 */
export function PlanUsersDialog({
  open,
  onOpenChange,
  kind,
  planId,
  planName,
}: PlanUsersDialogProps) {
  const voice = useVoicePlanUsers(kind === 'voice' && open ? planId : null);
  const tts = useTtsPlanUsers(kind === 'tts' && open ? planId : null);
  const query = kind === 'voice' ? voice : tts;

  if (!open) return null;

  const users = query.data ?? [];
  const kindLabel = kind === 'voice' ? 'Voice Plan' : 'TTS Plan';
  const onClose = () => onOpenChange(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[70vh] w-full max-w-lg overflow-hidden rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">
            {kindLabel} {planName ? `"${planName}"` : ''} — Users ({users.length})
          </h3>
          <button onClick={onClose} className="text-lg leading-none text-muted-foreground hover:text-foreground">
            ✕
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {query.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading...</p>
          ) : users.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No users are on this plan yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">User ID</th>
                  <th className="px-4 py-2 text-left font-medium">Username</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId} className={cn('border-t')}>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{u.userId}</td>
                    <td className="px-4 py-2">{u.username || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}